import React, { useState } from 'react';
import { FadeIn } from './ui/FadeIn';
import { ArrowRight, ShieldCheck, Check, ChevronDown, ChevronUp, Plus, Star } from 'lucide-react';
import { trackAndRedirect } from '../utils/metaPixel';
export function CTASection() {
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const basePrice = 6.36;
  const bumpPrice = 9;
  const bumpsSelected = (bump1 ? 1 : 0) + (bump2 ? 1 : 0);
  const totalPrice = basePrice + bumpsSelected * bumpPrice;
  const totalSavings = (144 - totalPrice).toFixed(2);
  const savingsPercent = Math.round((144 - totalPrice) / 144 * 100);
  // Shopify permalinks for each combination
  const getCheckoutUrl = () => {
    if (bump1 && bump2) return 'https://www.betterbysteps.com/cart/44201724215389:1';
    if (bump1) return 'https://www.betterbysteps.com/cart/44201712287837:1';
    if (bump2) return 'https://www.betterbysteps.com/cart/44201732145245:1';
    return 'https://www.betterbysteps.com/cart/44196935958621:1';
  };
  const bonuses = [{
    src: 'https://cdn.magicpatterns.com/uploads/dfPmBeaVyeUoQ4kft4m2c9/BONO_1_CPAP.png',
    alt: 'The 2:07 AM Re-Engagement Protocol',
    title: '2:07 AM Protocol',
    value: '$19'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/3PhaFCJjwk5oNnyHBUypeM/bono_2_cpap_.png',
    alt: 'Leak-to-Dry-Mouth Decoder',
    title: 'Leak Decoder',
    value: '$17'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/ucwmydf3E1eQ2cm5oNyWYp/bono_3_CPAP_.png',
    alt: 'Doctor-Ready CPAP Visit Pack',
    title: 'Doctor Visit Pack',
    value: '$11'
  }];
  const bonusDetails = [{
    label: 'Bonus #1',
    title: 'The 2:07 AM Re‑Engagement Protocol (65+)',
    tagline: '"I\'m doing fine… until I wake up and rip it off."',
    desc: 'A 3‑minute nightstand script that helps you stop the panic loop before your hands remove the mask.',
    bullets: ['Step‑by‑step "2:07 AM" protocol — re-seal quickly without over-tightening', 'One‑page printable nightstand card — instant access by the bed', '7‑day re‑engagement tracker — build a streak instead of quitting']
  }, {
    label: 'Bonus #2',
    title: 'Leak‑to‑Dry‑Mouth Decoder (Beard + Mouth Breather Edition)',
    tagline: 'Dry mouth isn\'t "just dry mouth." If you guess wrong, you waste nights.',
    desc: 'A 90‑second decision path to figure out the real cause—then pick the minimum effective fix for tonight.',
    bullets: ['Simple "cause → fix" flow — mouth leaks vs. beard leaks vs. humidity issues', 'Printable tracking sheets — track dryness, leaks, nasal blockage, and rainout', '"Humidity vs Rainout" dial sheet — stop over-adjusting and guessing']
  }, {
    label: 'Bonus #3',
    title: 'Doctor‑Ready CPAP Visit Pack (Bring This to Your Provider)',
    tagline: "Don't leave your appointment with the same generic advice.",
    desc: 'Walk in like the prepared patient who gets taken seriously.',
    bullets: ['A 60‑second script — exactly what to say first so they listen', "One‑page symptom summary — printable so you don't forget details", 'Question checklist & evidence log — leave with a real plan']
  }];
  return <section id="pricing" className="py-14 px-4 bg-navy-900 text-white text-center" data-id="element-176">
      <div className="max-w-4xl mx-auto" data-id="element-177">
        <FadeIn data-id="element-178">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-179">
            Stop Ripping Off Your Mask. Start Sleeping.
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-180">
            If you're tired of "just get used to it" advice—and you want a calm,
            step-by-step plan to get through the first month without quitting:
          </p>

          {/* Unified card: bonuses + pricing */}
          <div className="bg-navy-800 rounded-2xl border border-navy-700 max-w-2xl mx-auto shadow-2xl overflow-hidden" data-id="element-181">
            {/* Bonuses area */}
            <div className="p-6 sm:p-8 md:p-10 pb-0 sm:pb-0 md:pb-0" data-id="element-182">
              <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-4" data-id="element-183">
                {bonuses.map((bonus, i) => <div key={i} className="flex flex-col items-center" data-id="element-184">
                    <div className="rounded-xl overflow-hidden border border-navy-600 mb-3" data-id="element-185">
                      <img src={bonus.src} alt={bonus.alt} className="w-full h-auto block" data-id="element-186" />
                    </div>
                    <span className="text-xs sm:text-sm text-blue-100 font-medium leading-tight text-center" data-id="element-187">
                      {bonus.title}
                    </span>
                    <span className="text-xs text-amber-500 font-bold" data-id="element-188">
                      {bonus.value} FREE
                    </span>
                  </div>)}
              </div>

              {/* Read more toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="inline-flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 font-medium transition-colors mb-4 focus:outline-none" data-id="element-189">
                {showBonusDetails ? 'Show less' : 'Read more about the bonuses'}
                {showBonusDetails ? <ChevronUp className="h-4 w-4" data-id="element-190" /> : <ChevronDown className="h-4 w-4" data-id="element-191" />}
              </button>

              {/* Expandable bonus details */}
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showBonusDetails ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-192">
                <div className="space-y-6 text-left pb-4" data-id="element-193">
                  {bonusDetails.map((bonus, i) => <div key={i} className="bg-navy-900/50 rounded-xl p-5 border border-navy-600" data-id="element-194">
                      <span className="text-amber-500 font-bold text-xs uppercase tracking-wider" data-id="element-195">
                        {bonus.label}
                      </span>
                      <h4 className="text-base sm:text-lg font-bold text-white mt-1 mb-2 font-serif" data-id="element-196">
                        {bonus.title}
                      </h4>
                      <p className="text-sm text-blue-200 italic mb-3 border-l-2 border-teal-600 pl-3" data-id="element-197">
                        {bonus.tagline}
                      </p>
                      <p className="text-sm text-blue-100 mb-3" data-id="element-198">{bonus.desc}</p>
                      <ul className="space-y-2" data-id="element-199">
                        {bonus.bullets.map((bullet, j) => <li key={j} className="flex items-start text-sm text-blue-100" data-id="element-200">
                            <Check className="h-4 w-4 text-teal-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-201" />
                            <span data-id="element-202">{bullet}</span>
                          </li>)}
                      </ul>
                    </div>)}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-navy-600" data-id="element-203" />
            </div>

            {/* Value breakdown */}
            <div className="px-6 sm:px-8 md:px-10 pt-6 sm:pt-8" data-id="element-204">
              <p className="text-sm text-gray-400 uppercase tracking-wider font-semibold mb-4" data-id="element-205">
                Here's what you're getting today
              </p>

              <div className="space-y-3 text-left max-w-sm mx-auto" data-id="element-206">
                <div className="flex items-center justify-between" data-id="element-207">
                  <div className="flex items-center" data-id="element-208">
                    <Check className="h-4 w-4 text-teal-400 mr-3 flex-shrink-0" data-id="element-209" />
                    <span className="text-sm sm:text-base text-blue-100" data-id="element-210">
                      CPAP Compliance Rescue Guide
                    </span>
                  </div>
                  <span className="text-sm text-gray-400 line-through ml-3" data-id="element-211">
                    $97
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-212">
                  <div className="flex items-center" data-id="element-213">
                    <Check className="h-4 w-4 text-teal-400 mr-3 flex-shrink-0" data-id="element-214" />
                    <span className="text-sm sm:text-base text-blue-100" data-id="element-215">
                      2:07 AM Re-Engagement Protocol
                    </span>
                  </div>
                  <span className="text-sm text-gray-400 line-through ml-3" data-id="element-216">
                    $19
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-217">
                  <div className="flex items-center" data-id="element-218">
                    <Check className="h-4 w-4 text-teal-400 mr-3 flex-shrink-0" data-id="element-219" />
                    <span className="text-sm sm:text-base text-blue-100" data-id="element-220">
                      Leak-to-Dry-Mouth Decoder
                    </span>
                  </div>
                  <span className="text-sm text-gray-400 line-through ml-3" data-id="element-221">
                    $17
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-222">
                  <div className="flex items-center" data-id="element-223">
                    <Check className="h-4 w-4 text-teal-400 mr-3 flex-shrink-0" data-id="element-224" />
                    <span className="text-sm sm:text-base text-blue-100" data-id="element-225">
                      Doctor-Ready CPAP Visit Pack
                    </span>
                  </div>
                  <span className="text-sm text-gray-400 line-through ml-3" data-id="element-226">
                    $11
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-227">
                  <div className="flex items-center" data-id="element-228">
                    <Check className="h-4 w-4 text-teal-400 mr-3 flex-shrink-0" data-id="element-229" />
                    <span className="text-sm sm:text-base text-blue-100" data-id="element-230">
                      7 Printable Tools & Trackers
                    </span>
                  </div>
                  <span className="text-sm text-gray-400 line-through ml-3" data-id="element-231">
                    included
                  </span>
                </div>
              </div>

              {/* Total value line */}
              <div className="border-t border-navy-600 mt-5 pt-4 max-w-sm mx-auto" data-id="element-232">
                <div className="flex items-center justify-between text-sm" data-id="element-233">
                  <span className="text-gray-400 font-medium" data-id="element-234">Total Value</span>
                  <span className="text-gray-400 line-through font-semibold" data-id="element-235">
                    $144
                  </span>
                </div>
              </div>
            </div>

            {/* Pricing area */}
            <div className="p-6 sm:p-8 md:p-10 pt-4 sm:pt-6" data-id="element-236">
              <div className="mb-8" data-id="element-237">
                <div className="inline-flex items-center bg-teal-600 text-white text-sm font-bold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wide" data-id="element-238">
                  You save ${totalSavings} — {savingsPercent}% off
                </div>

                <span className="block text-sm text-gray-400 uppercase tracking-widest font-semibold mb-1" data-id="element-239">
                  Today Only
                </span>
                <span className="block text-lg text-gray-300 uppercase tracking-widest font-semibold" data-id="element-240">
                  One-Time Payment
                </span>

                <div className="flex items-center justify-center gap-4 mt-4" data-id="element-241">
                  <span className="text-2xl text-gray-500 line-through font-medium" data-id="element-242">
                    $144
                  </span>
                  <span className="text-6xl font-bold text-white transition-all duration-300" data-id="element-243">
                    ${totalPrice.toFixed(2)}
                  </span>
                </div>

                <p className="mt-3 text-sm text-teal-400 font-medium transition-all duration-300" data-id="element-244">
                  {bumpsSelected === 0 ? 'Launch price — complete guide + 3 bonus tools + 7 printables' : bumpsSelected === 1 ? 'Launch price — complete guide + 3 bonus tools + 7 printables + 1 add-on' : 'Launch price — complete guide + 3 bonus tools + 7 printables + 2 add-ons'}
                </p>
              </div>

              {/* Primary CTA button — dynamic based on selection */}
              <a href={getCheckoutUrl()} target="_blank" rel="noopener noreferrer" onClick={e => {
              e.preventDefault();
              trackAndRedirect(getCheckoutUrl());
            }} className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xl font-bold py-5 px-8 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center" data-id="element-245">
                Download CPAP Compliance Rescue
                <ArrowRight className="ml-2 h-6 w-6" data-id="element-246" />
              </a>

              {/* Order Bumps */}
              <div className="mt-6 space-y-3" data-id="element-247">
                {/* Highly Recommended label */}
                <div className="flex items-center justify-center gap-2 mb-1" data-id="element-248">
                  <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" data-id="element-249" />
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-widest" data-id="element-250">
                    Highly Recommended
                  </span>
                  <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" data-id="element-251" />
                </div>

                {/* Order Bump #1 */}
                <button onClick={() => setBump1(!bump1)} className={`w-full text-left rounded-lg border-2 border-dashed transition-all duration-200 overflow-hidden relative ${bump1 ? 'border-amber-500 bg-amber-500/10' : 'border-navy-600 bg-navy-900/50 hover:border-navy-500'}`} data-id="element-252">
                  {!bump1 && <div className="absolute inset-0 bump-shimmer pointer-events-none rounded-lg" data-id="element-253" />}
                  <div className="px-4 py-3 flex items-center gap-1.5 bg-amber-500/20 border-b border-dashed border-amber-500/30 relative z-10" data-id="element-254">
                    <Plus className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" data-id="element-255" />
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-wider" data-id="element-256">
                      One-time offer — Add to your order
                    </span>
                  </div>
                  <div className="p-4 flex gap-4 relative z-10" data-id="element-257">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1" data-id="element-258">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${bump1 ? 'bg-amber-500 border-amber-500' : 'border-gray-500 bg-transparent'}`} data-id="element-259">
                        {bump1 && <Check className="h-3.5 w-3.5 text-white" data-id="element-260" />}
                      </div>
                    </div>
                    {/* Image */}
                    <div className="flex-shrink-0 w-16 sm:w-20" data-id="element-261">
                      <img src="https://cdn.magicpatterns.com/uploads/kH8jp4NsYhSWnYLy5NV4oD/order_bump1_.png" alt="CPAP Nightstand Setup Checklist" className="w-full h-auto rounded-md" data-id="element-262" />
                    </div>
                    {/* Text */}
                    <div className="flex-1 min-w-0" data-id="element-263">
                      <h4 className="text-sm sm:text-base font-bold text-white leading-tight mb-1" data-id="element-264">
                        Remove the "2am Friction" (10‑Minute Nightstand Setup){' '}
                        <span className="text-amber-400 font-bold" data-id="element-265">— $9</span>
                      </h4>
                      <p className="text-xs sm:text-sm text-blue-200 leading-relaxed" data-id="element-266">
                        A printable setup map that shows you exactly how to
                        place your machine, hose, mask, water, and quick‑fix
                        items so you can handle small disruptions half‑asleep.
                        Includes: Nightstand Layout Map + Before‑Bed Routine
                        Card + Quick Fix Card.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Order Bump #2 */}
                <button onClick={() => setBump2(!bump2)} className={`w-full text-left rounded-lg border-2 border-dashed transition-all duration-200 overflow-hidden relative ${bump2 ? 'border-amber-500 bg-amber-500/10' : 'border-navy-600 bg-navy-900/50 hover:border-navy-500'}`} data-id="element-267">
                  {!bump2 && <div className="absolute inset-0 bump-shimmer pointer-events-none rounded-lg" data-id="element-268" />}
                  <div className="px-4 py-3 flex items-center gap-1.5 bg-amber-500/20 border-b border-dashed border-amber-500/30 relative z-10" data-id="element-269">
                    <Plus className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" data-id="element-270" />
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-wider" data-id="element-271">
                      One-time offer — Add to your order
                    </span>
                  </div>
                  <div className="p-4 flex gap-4 relative z-10" data-id="element-272">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1" data-id="element-273">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${bump2 ? 'bg-amber-500 border-amber-500' : 'border-gray-500 bg-transparent'}`} data-id="element-274">
                        {bump2 && <Check className="h-3.5 w-3.5 text-white" data-id="element-275" />}
                      </div>
                    </div>
                    {/* Image */}
                    <div className="flex-shrink-0 w-16 sm:w-20" data-id="element-276">
                      <img src="https://cdn.magicpatterns.com/uploads/pnadTtgojsW8AHMohKvrCn/order_bump_2.png" alt="CPAP 7-Day Progress Tracker" className="w-full h-auto rounded-md" data-id="element-277" />
                    </div>
                    {/* Text */}
                    <div className="flex-1 min-w-0" data-id="element-278">
                      <h4 className="text-sm sm:text-base font-bold text-white leading-tight mb-1" data-id="element-279">
                        The 7‑Day CPAP Streak Tracker (2 Minutes Each Morning){' '}
                        <span className="text-amber-400 font-bold" data-id="element-280">— $9</span>
                      </h4>
                      <p className="text-xs sm:text-sm text-blue-200 leading-relaxed" data-id="element-281">
                        In 2 minutes each morning, record the few things that
                        matter most (hours used, mask-off moments, comfort,
                        dryness, and leak-by-feel) so you can spot patterns fast
                        and stop changing random things. Includes: Daily
                        Snapshot Sheet + 7‑Day Trend Dashboard + One‑Change Rule
                        Card.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Dynamic checkout button based on bump selection */}
              <a href={getCheckoutUrl()} target="_blank" rel="noopener noreferrer" onClick={e => {
              e.preventDefault();
              trackAndRedirect(getCheckoutUrl());
            }} className="mt-5 w-full bg-amber-500 hover:bg-amber-600 text-white text-lg sm:text-xl font-bold py-5 px-8 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center" data-id="element-282">
                {bumpsSelected > 0 ? `Complete Your Order — $${totalPrice.toFixed(2)}` : 'Download CPAP Compliance Rescue'}
                <ArrowRight className="ml-2 h-6 w-6" data-id="element-283" />
              </a>

              <div className="mt-6 flex items-center justify-center text-sm text-gray-400" data-id="element-284">
                <ShieldCheck className="h-4 w-4 mr-2" data-id="element-285" />
                Secure 256-bit SSL Encrypted Checkout
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}